import { useEffect, useMemo, useState } from "react";

import { Info24px, Platforms } from "@similarweb/icons";
import { colorsPalettes } from "@similarweb/styles";
import { Combobox, DropdownButton } from "@similarweb/ui-components";
import { Box, Flex, Text } from "@similarweb/ui-components/foundations";

import { onHookClick } from "app/components/sidebar3/SideBarItems";
import { translate } from "app/filters/i18nFilter";
import { SetupFieldLoader } from "app/pages/digital-marketing/generative-ai/llm-tracker/homepage/components/SetupFieldLoader";
import { AI_MODELS_CONFIG } from "app/pages/digital-marketing/generative-ai/llm-tracker/shared/LLMConsts";
import type { ICountryObject } from "app/services/CountryService";
import { EHooksVersions } from "app/services/SwHook";
import { allTrackers } from "app/services/track/track";

import type { ExplicitLLModel } from "../../types";

import { ModelIcon } from "./ModelIcon";
import { DropdownOptionText } from "./ModelSelector.styled";
import { ModelSelectorItem } from "./ModelSelectorItem";

interface ILLMSetupModelSelector {
    selectedModels: ExplicitLLModel[];
    supportedModels: ExplicitLLModel[];
    selectedCountry: ICountryObject;
    isLoading: boolean;
    isReadOnly?: boolean;
    onChange: (models: ExplicitLLModel[]) => void;
}

export const ModelSelector = ({
    selectedModels,
    supportedModels,
    selectedCountry,
    isLoading,
    isReadOnly,
    onChange,
}: ILLMSetupModelSelector) => {
    const [isOpen, setOpen] = useState<boolean>(false);
    const [forciblyRemovedUnsupportedModels, setForciblyRemovedUnsupportedModels] = useState<ExplicitLLModel[]>();
    const allModels = useMemo(
        () => Object.fromEntries(Object.entries(AI_MODELS_CONFIG).filter(([, config]) => !config.isNotSupported)),
        [],
    );

    const changedModelIndicationLabel = useMemo<string>(() => {
        const modelsCount = forciblyRemovedUnsupportedModels?.length ?? 0;

        if (!modelsCount) {
            return "";
        } else if (modelsCount === 1) {
            const model = forciblyRemovedUnsupportedModels![0];
            const modelName = translate(AI_MODELS_CONFIG[model]?.displayNameKey);
            return translate("llm.setup.models.indication.single", {
                model: modelName,
                country: selectedCountry.text,
            });
        } else {
            return translate("llm.setup.models.indication.plural", {
                country: selectedCountry.text,
            });
        }
    }, [forciblyRemovedUnsupportedModels, selectedCountry]);

    const selectedModelsLabel = useMemo<string>(() => {
        const allModelKeys = Object.keys(allModels) as ExplicitLLModel[];
        return allModelKeys
            .filter((model) => selectedModels.includes(model))
            .map((model) => translate(AI_MODELS_CONFIG[model].displayNameKey))
            .join(", ");
    }, [allModels, selectedModels]);

    useEffect(() => {
        if (isLoading || isReadOnly) {
            return;
        }

        const stillSupported: ExplicitLLModel[] = [];
        const nonSupported: ExplicitLLModel[] = [];
        selectedModels.forEach((model) => {
            if (supportedModels.includes(model)) {
                stillSupported.push(model);
            } else {
                nonSupported.push(model);
            }
        });

        if (stillSupported.length === selectedModels.length) {
            setForciblyRemovedUnsupportedModels([]);
            return;
        }

        setForciblyRemovedUnsupportedModels(nonSupported);

        if (stillSupported.length) {
            onChange(stillSupported);
        } else {
            onChange(supportedModels.length ? [supportedModels[0]] : []);
        }
    }, [supportedModels, isLoading, isReadOnly]);

    const handleModelSelection = (model: ExplicitLLModel): void => {
        const modelName = translate(AI_MODELS_CONFIG[model].displayNameKey);
        allTrackers.dispatchTrackEvent("Dropdown", "Select", `Ai Brand Visibility/Models/${modelName}`);

        if (selectedModels.includes(model)) {
            if (selectedModels.length > 1) {
                onChange(selectedModels.filter((m) => m !== model));
            }
        } else {
            onChange([...selectedModels, model]);
        }
    };

    const onToggle = (state: boolean): void => {
        if (state && isReadOnly) {
            return;
        }

        if (state) {
            allTrackers.dispatchTrackEvent("Dropdown", "Open", "Ai Brand Visibility/Models");
        }

        setOpen(state);
    };

    const handleLockedModelClick = (): void => {
        setOpen(false);
        onHookClick("AiTracker", "digitalsuite_llm_solutions_homepage", EHooksVersions.V5);
    };

    return (
        <Flex
            flexDirection="column"
            gap="4px"
        >
            <Box
                position="relative"
                borderRadius="3px"
                height="40px"
            >
                {isLoading ? (
                    <SetupFieldLoader />
                ) : (
                    <Combobox
                        isOpen={isOpen}
                        onOpenChange={onToggle}
                        placement="bottom-start"
                        matchTriggerWidth
                        data-automation="llm-setup-models-dropdown-popup"
                    >
                        <Combobox.Trigger>
                            <Flex width="100%">
                                <DropdownButton
                                    disabled={isReadOnly}
                                    isOpen={isOpen}
                                    hasValue={selectedModels.length === 1}
                                    dataAutomation="llm-setup-models-dropdown"
                                >
                                    <Flex
                                        gap="8px"
                                        justifyContent="center"
                                        alignItems="center"
                                        margin="0 0 0 15px"
                                    >
                                        {selectedModels.length === 1 ? (
                                            <ModelIcon model={selectedModels[0]} />
                                        ) : (
                                            <Platforms size="sm" />
                                        )}
                                        <DropdownOptionText
                                            fontVariant="input2"
                                            color={colorsPalettes.carbon[400]}
                                        >
                                            {selectedModelsLabel}
                                        </DropdownOptionText>
                                    </Flex>
                                </DropdownButton>
                            </Flex>
                        </Combobox.Trigger>
                        <Combobox.Content>
                            {(Object.keys(allModels) as ExplicitLLModel[]).map((model) => (
                                <ModelSelectorItem
                                    key={model}
                                    model={model}
                                    isSelected={selectedModels.includes(model)}
                                    onClick={() => handleModelSelection(model)}
                                    onLockedClick={handleLockedModelClick}
                                    isDisabled={!supportedModels.includes(model)}
                                    selectedCountry={selectedCountry}
                                />
                            ))}
                        </Combobox.Content>
                    </Combobox>
                )}
            </Box>
            {changedModelIndicationLabel && !isLoading ? (
                <Flex
                    alignItems="center"
                    gap="4px"
                >
                    <Info24px
                        size="xs"
                        color={colorsPalettes.red["400_P"]}
                    />
                    <Text
                        fontVariant="caption"
                        color={colorsPalettes.red["400_P"]}
                    >
                        {changedModelIndicationLabel}
                    </Text>
                </Flex>
            ) : null}
        </Flex>
    );
};
